/* eslint-disable react-hooks/rules-of-hooks */
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { isEqual } from "lodash";
import { useRef } from "react";
import * as Yup from "yup";
import { useClearEnrollingConditionSet } from "../hooks/useClearEnrollingConditionSet";
import { useScopes } from "../hooks/useScopes";
import { SelectableValue } from "../models/common/selectable-value";
import { ControlState } from "../models/flexible/control-state";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { InitialRequestConfig } from "../models/flexible/initial-request";
import { isStartingPoint } from "../models/flexible/typed-flex-entity";
import { ConditionLogicalOperator, Maybe } from "../models/generated/graphql";
import { distinctBy } from "../utils/distinct-by";
import { GeneralFieldController } from "./general-field-controller";
import { GET_SCOPES, whereDrugs } from "./queries";

function CombinedTreatmentSelectionEffect(props: { path: string; section: FlexSectionController }) {
	const { selectedScopes } = useScopes(props.section.context);
	const filteredScopes = selectedScopes.filter((scope) => scope.logicalOperator === ConditionLogicalOperator.And);
	const scopes = distinctBy(filteredScopes, (scope) => scope.scopeId);
	const scopeRef = useRef(scopes);
	if (!isEqual(scopeRef.current, scopes)) {
		scopeRef.current = scopes;
	}

	return null;
}

export class ScopeIdFieldController extends GeneralFieldController {
	getInitialRequestComponents?(): InitialRequestConfig {
		return {
			queries: [GET_SCOPES],
			variables: { where: whereDrugs(this.parent.context.entityType.id, this.parent.context.tenantId) },
		};
	}

	override mapInitialValue(value: unknown): unknown {
		if (value || !this.parent.context.cases.length) {
			return super.mapInitialValue(value);
		}

		const caseInstance = this.parent.context.cases[0];
		const scopeId =
			caseInstance.flexEntities.find((entity) => entity.data[AttributeIdentifiers.ScopeId])?.data[AttributeIdentifiers.ScopeId] ||
			null;
		return super.mapInitialValue(scopeId);
	}

	override useControlState(): ControlState {
		return !this.parent.context.cases?.length || isStartingPoint(this.parent.context.flexEntity) ? "enabled" : "disabled";
	}

	override useControlProps(): Maybe<FlexControlProps> | undefined {
		const { selectedScopesExactlyMatchedByDrugs: selectedScopes } = useScopes(this.parent.context);

		const handleClear = useClearEnrollingConditionSet(this.parent.context);
		const options = distinctBy(
			selectedScopes
				.flatMap(
					(scope) =>
						scope.orgScopes
							?.map((scope) => scope.organizationScopeId || "")
							.filter(Boolean)
							.map((scopeId) => ({ value: scopeId, label: scopeId } as SelectableValue)) || []
				)
				.concat(
					selectedScopes
						.filter((scope) => scope.countryScopesConnection.edges.some((edge) => edge.countryScopeId))
						.map((scope) => {
							const filteredEdges = distinctBy(
								scope.countryScopesConnection.edges.filter((edge) => edge.countryScopeId),
								(item) => item.countryScopeId
							);
							return {
								value: filteredEdges.map((edge) => edge.countryScopeId).find(Boolean),
								label: filteredEdges
									.map((edge) => edge.countryScopeId)
									.filter(Boolean)
									.sort()
									.join("; "),
							} as SelectableValue;
						})
				)
				.filter(Boolean),
			(x) => x.label
		);

		return {
			controlType: "typeahead",
			options,
			optionLabel: "label",
			property: "value",
			selectPropertyValue: true,
			shouldAutofill: selectedScopes.length === 1,
			getOptionSelected: (option: SelectableValue, value: string) => option.label.split("; ").includes(value),
			onClear: handleClear,
		};
	}

	override getValueTypeProps() {
		return {
			controlType: "typeahead",
			property: "label",
		};
	}

	renderBefore(path: string) {
		return <CombinedTreatmentSelectionEffect path={path} section={this.parent} />;
	}

	protected override createFieldValidationRuleByDataType(): Yup.AnySchema {
		return Yup.string().nullable();
	}
}
